import React from "react";

import cx from "classnames";
import { noop } from "lodash";

import { getFirstUserFacingError } from "~/lib/utils";
import { toast } from "~/ui";

import ContextPill, { ContextSuggestion } from "./AskFellowContext";
import { useCreateConversationMutation } from "./AskFellowHooks";
import { type AskCopilotConversation, MessageForm } from "./AskFellowPanelInner";
import SuggestedQuestionsWrapper from "./AskFellowSuggestedQuestions";

import styles from "./AskFellowPanel.module.css";

interface AskFellowLandingPageProps {
	headerLabel: string;
	conversation: AskCopilotConversation;
	compactLandingPage?: boolean;
	onMessageFormSubmit: (content: string) => void;
	aiFeaturesDisabled: boolean;
	contextFooterRef: React.RefObject<HTMLDivElement>;
	contextId?: string;
	refetch: () => void;
	sendMessage: (content: string, messageId?: string, contentForDisplay?: string) => void;
}

const AskFellowLandingPage: React.FC<AskFellowLandingPageProps> = ({
	headerLabel,
	conversation,
	compactLandingPage,
	onMessageFormSubmit,
	aiFeaturesDisabled,
	contextFooterRef,
	sendMessage,
	contextId,
	refetch,
}) => {
	const [createConversation, mutationInProgress] = useCreateConversationMutation();
	const createConversationWithNoteContext = React.useCallback(async () => {
		if (mutationInProgress || !contextId) return;

		try {
			await createConversation(contextId);
		} catch (e) {
			const message = getFirstUserFacingError(e) ?? "Something went wrong; please try again later.";
			toast.error(message);
		}
	}, [contextId, createConversation, mutationInProgress]);

	React.useEffect(() => {
		createConversationWithNoteContext().then(() => {
			refetch();
		});
	}, [contextId]); // eslint-disable-line react-hooks/exhaustive-deps

	const isContextSet = conversation?.context;

	if (!conversation) return null;

	return (
		<div className={cx(styles.landingPageContainer, { [styles.compactLandingPage]: compactLandingPage })}>
			<div className={styles.landingPageHeader}>
				<h2 className={styles.landingPageTitle}>{headerLabel}</h2>
			</div>
			<MessageForm
				conversation={conversation}
				onSubmit={onMessageFormSubmit}
				currentlyGenerating={false}
				canStopGenerating={false}
				onStop={noop}
				reachedMessageLimit={conversation?.reachedMessageLimit ?? false}
				aiFeaturesDisabled={aiFeaturesDisabled}
				showAIDisclaimer={false}
				supportPreFilledQuery={true}
				footer={
					<ContextPill
						conversationKey={conversation}
						className={styles.contextPill}
						contextFooterRef={contextFooterRef}
					/>
				}
				contextFooterRef={contextFooterRef}
			/>
			{conversation?.id && !isContextSet && <ContextSuggestion conversationId={conversation.id} />}
			<SuggestedQuestionsWrapper
				conversation={conversation}
				sendMessage={sendMessage}
				aiFeaturesDisabled={aiFeaturesDisabled}
			/>
		</div>
	);
};

export default AskFellowLandingPage;
